const T = require('prop-types');
const { makeActionCreator } = require('../utils/redux-helpers');
const { COMMUNICATION: Types } = require('../action-types');

const { analyticsTemplates } = require('../utils/analytics');
const { DEFAULT_ROLE_GROUP_FILTERS } = require('utils/constants');
const CreateCommunicationService = require('services/communication');

module.exports = (context) => {

    const api = context.api.nearpeer;
    const communication = CreateCommunicationService.fromContext(context);
    const actions = context.actions;
    const redux = context.redux.hooks;
    const selectors = context.selectors.all;
    const sendMessageAction = makeActionCreator(Types.SEND_MESSAGE, {
        channelSid: T.string, message: T.string, clientMessageId: T.string
    }, { messageIndex: T.number, clientMessageId: T.string }, { async: communication.sendMessage });
    const sendMessage = Object.assign((request) => async (dispatch, getState) => {

        let failure;
        const result = await sendMessageAction(request)((action) => {

            if (action.type === Types.SEND_MESSAGE.FAILURE) {
                failure = action;
            }

            return dispatch(action);
        }, getState);

        // Other async actions swallow failures. Chat needs this specific result
        // to settle the matching optimistic row without changing global behavior.
        return failure || result;
    }, sendMessageAction);

    const refreshChannelLastMessageAfterRemove = ({ dispatch, channelSid }) => {

        dispatch(actions.dataFetching.clearChannelLastMessage({ channelSid }));
        return dispatch(actions.dataFetching.fetchChannelLastMessage({ channelSid }));
    };

    return {
        requestPeer: makeActionCreator(Types.REQUEST_PEER, {
            userId: T.string,               // When not being used as batch.
            userIds: T.arrayOf(T.string),   // When being used as batch.
            message: T.string,               // Don't use both userId and userIds!
            similarityText: T.string,               // Don't use both userId and userIds!
            isAutoConnect: T.bool
        }, {
            async: api.communication.requestPeer,
            after: ({ dispatch, payload: { request } }) => {

                const { userId, userIds,isAutoConnect } = request;

                const requestsTerm = (() => {

                    if (userId) {
                        return 'request';
                    }

                    if (userIds && (userIds.length === 1)) {
                        return 'request';
                    }

                    return 'requests';
                })();

                dispatch(actions.dataFetching.fetchSync());

                if (isAutoConnect) {
                    dispatch(actions.dataFetching.fetchChannels());
                }
                else {
                    dispatch(actions.alerts.notification.push({ message: `Peer ${requestsTerm} sent!` }));
                }
            }
        }),
        createTwilioConversation: makeActionCreator(Types.CREATE_TWILIO_CONVERSATION, {
            user1Id: T.string,
            user2Id: T.string
        }, {
            async: api.communication.createTwilioConversation
        }),
        disconnect: makeActionCreator(Types.DISCONNECT_PEER, { userId: T.string, firstName: T.string }, {
            async: api.communication.declinePeer,
            after: ({ dispatch, payload: { request } }) => {

                dispatch(actions.dataFetching.fetchSync());
                dispatch(actions.alerts.notification.push({ message: `Disconnected from ${request.firstName}` }));
            }
        }),
        acceptPeer: makeActionCreator(Types.ACCEPT_PEER, { userId: T.string, firstName: T.string }, {
            async: (args) => Promise.all([api.communication.acceptPeer(args)]).then(() => undefined),
            after: ({ dispatch, payload: { request } }) => {

                analyticsTemplates.buttons('confirm peer request from user', 'profile: confirm peer request');
                dispatch(actions.dataFetching.fetchSync());
                dispatch(actions.dataFetching.fetchChannels());
                dispatch(actions.alerts.notification.push({ message: `You are now connected to ${request.firstName}` }));
            }
        }),
        enableUser: makeActionCreator(Types.ENABLE_USER, { userId: T.string, toEnable: T.boolean }, {
            async: api.communication.enableUser,
            after: ({ dispatch, payload: { request } }) => {

                const toEnableLanguage = request.toEnable ? 'enabled' : 'disabled';
                dispatch(actions.dataFetching.fetchUser({ id: request.userId }));
                dispatch(actions.alerts.notification.push({ message: `Successfully ${toEnableLanguage} user` }));

                // Set to re-submit discover search
                dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

                redux.dispatch(actions.app.setRoleGroupFilter(DEFAULT_ROLE_GROUP_FILTERS));

                // Refetch school details
                const currentSchool = selectors.getCurrentSchool(redux.getState());
                dispatch(actions.dataFetching.fetchAppEntities({
                    entities: ['schoolDetails'],
                    schoolId: currentSchool.id
                }));
            }
        }),
        changeUserRole: makeActionCreator(Types.CHANGE_USER_ROLE, { userId: T.string, roleId: T.number }, {
            async: api.communication.changeUserRole,
            after: ({ dispatch, payload: { request } }) => {

                dispatch(actions.dataFetching.fetchUser({ id: request.userId }));
                dispatch(actions.alerts.notification.push({ message: 'Successfully changed user role' }));

                // Role affects discover/group visibility, so re-run those searches
                dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));
                redux.dispatch(actions.app.setRoleGroupFilter(DEFAULT_ROLE_GROUP_FILTERS));

                const currentSchool = selectors.getCurrentSchool(redux.getState());
                dispatch(actions.dataFetching.fetchAppEntities({
                    entities: ['schoolDetails'],
                    schoolId: currentSchool.id
                }));
            }
        }),
        sendMessage,
        updateMessageBody: makeActionCreator(Types.UPDATE_MESSAGE_BODY, { sid: T.string, body: T.string }, { message: T.object }, {
            async: communication.updateMessageBody
        }),
        sendAnnouncementMessage: makeActionCreator(Types.SEND_ANNOUNCEMENT_MESSAGE, { classId: T.number, message: T.string }, { entities: T.object, result: T.oneOfType([T.string, T.number]) }, {
            async: api.communication.sendAnnouncementMessage,
            after: ({ dispatch, payload: { request: { classId } } }) => {

                dispatch(actions.dataFetching.fetchAnnouncementMessages({ id: classId }));
                dispatch(actions.dataFetching.fetchAnnouncementConversations());
            }
        }),
        updateAnnouncementMessageBody: makeActionCreator(Types.UPDATE_ANNOUNCEMENT_MESSAGE_BODY, { messageId: T.string,conversationId: T.string,classId: T.string, body: T.string }, { message:T.object },{
            async: api.communication.updateAnnouncementMessage,
            after: ({ dispatch, payload: { request: { classId } } }) => {

                dispatch(actions.dataFetching.fetchAnnouncementMessages({ id: classId }));
            }
        }),
        pinMessage: makeActionCreator(Types.PIN_MESSAGE, { channelSid: T.string, messageSid: T.string, isPinned: T.bool }, {
            async: api.communication.pinMessage
        }),
        moderateMessage: makeActionCreator(Types.MODERATE_MESSAGE, { channelSid: T.string, messageSid: T.string, status: T.string }, {
            async: api.communication.moderateMessage
        }),
        pinAnnouncementMessage: makeActionCreator(Types.PIN_ANNOUNCEMENT_MESSAGE, { conversationId: T.string,classId: T.string, messageId: T.string, isPinned: T.bool }, {
            async: api.communication.pinAnnouncementMessage,
            after: ({ dispatch, payload: { request: { classId } } }) => {

                dispatch(actions.dataFetching.fetchAnnouncementMessages({ id: classId }));
            }
        }),
        moderateAnnouncementMessage: makeActionCreator(Types.MODERATE_ANNOUNCEMENT_MESSAGE, { conversationId: T.string,classId: T.string, messageId: T.string, status: T.oneOf(['ok', 'moderated']) }, {
            async: api.communication.moderateAnnouncementMessage,
            after: ({ dispatch, payload: { request: { classId } } }) => {

                dispatch(actions.dataFetching.fetchAnnouncementMessages({ id: classId }));
            }
        }),
        removeAnnouncementMessage: makeActionCreator(Types.REMOVE_ANNOUNCEMENT_MESSAGE, { conversationId: T.string, messageId: T.string, classId: T.string }, {
            async: api.communication.removeAnnouncementMessage,
            after: ({ dispatch, payload: { request: { classId } } }) => {

                dispatch(actions.dataFetching.fetchAnnouncementMessages({ id: classId }));
            }
        }),
        setAnnouncementLastReadMessageIndex: makeActionCreator(Types.SET_ANNOUNCEMENT_LAST_READ_MESSAGE_INDEX, { conversationId: T.string }, {
            async: api.communication.setAnnouncementLastReadMessageIndex,
            after: ({ dispatch }) => {

                dispatch(actions.dataFetching.fetchNotifications());
            }
        }),
        removeMessage: makeActionCreator(Types.REMOVE_MESSAGE, { channelSid: T.string, messageSid: T.string }, {
            async: communication.removeMessage,
            after: ({ dispatch, payload: { request } }) => {

                return refreshChannelLastMessageAfterRemove({ dispatch, channelSid: request.channelSid });
            }
        }),
        removeOwnMessage: makeActionCreator(Types.REMOVE_OWN_MESSAGE, { channelSid: T.string, messageSid: T.string }, {
            async: communication.removeMessage,
            after: ({ dispatch, payload: { request } }) => {

                return refreshChannelLastMessageAfterRemove({ dispatch, channelSid: request.channelSid });
            }
        }),
        setLastReadMessageIndex: makeActionCreator(Types.SET_LAST_READ_MESSAGE_INDEX, { channelSid: T.string, messageIndex: T.number }, {
            async: communication.markMessageAsLastRead,
            after: async ({ dispatch, payload: { request } }) => {

                await dispatch(actions.notifications.clearMentions({ channelSid: request.channelSid }));
            }
        }),
        setLastReadMessageIndex_byConversationSid: makeActionCreator(Types.SET_LAST_READ_MESSAGE_INDEX_BY_CONVERSATION_SID, { conversationSid: T.string, messageIndex: T.number }, {
            async: api.communication.setAnnouncementLastReadMessageIndex_ByConversationSid
        }),
        mention: makeActionCreator(Types.MENTION, { channelSid: T.string, messageSid: T.string }, {
            async: api.communication.mention
        }),
        flagMessageAsInappropriate: makeActionCreator(Types.FLAG_MESSAGE_AS_INAPPROPRIATE, { sid: T.string }, {
            async: ({ sid }) => {

                return api.appFeedback.submitBugReport({
                    bugType: 'Report message as inappropriate',
                    bugDetails: {
                        description: selectors.getMessageDetails_forFlagging(redux.getState(), sid)
                    }
                });
            },
            after: () => actions.alerts.notification.push({ message: 'Thanks for your feedback – we\'re on it!' })
        }),
        flagAnnouncementMessageAsInappropriate: makeActionCreator(Types.FLAG_ANNOUNCEMENT_MESSAGE_AS_INAPPROPRIATE, { id: T.string,classId: T.string }, {
            async: ({ id,classId }) => {

                return api.appFeedback.submitBugReport({
                    bugType: 'Report message as inappropriate',
                    bugDetails: {
                        description: selectors.getAnnouncementMessageDetails_forFlagging(redux.getState(), id,classId)
                    }
                });
            },
            after: () => actions.alerts.notification.push({ message: 'Thanks for your feedback – we\'re on it!' })
        }),
        targetNotifyUser: makeActionCreator(Types.TARGET_NOTIFY_USER, { userId: T.string, schoolId: T.string, text: T.string,emojiSymbol: T.string,usePushSystem: T.bool, startTime: T.string }, {
            async: api.communication.targetNotifyUser,
            after: ({ dispatch, payload: { request } }) => {

                const message = request.startTime ? 'Notification Scheduled!' : 'Notification sent!';
                dispatch(actions.alerts.notification.push({ message }));
                dispatch(actions.dataFetching.fetchScheduledNotifications());
            }
        }),
        batchTargetNotify: makeActionCreator(Types.BATCH_TARGET_NOTIFY, { sendViaNerpy: T.bool, schoolId: T.string, roleId: T.number,selectedUsers: T.array,excludedUserIds: T.array,usedCriteria: T.object, text: T.string,emojiSymbol: T.string,usePushSystem: T.bool, startTime: T.string, csv: T.any }, {
            async: api.communication.batchTargetNotify,
            after: ({ dispatch, payload: { request, result } }) => {

                let message;
                if (request.startTime) {
                    message = 'Notifications Scheduled!';
                }
                else if (result && result.notifiedCount === 0) {
                    message = 'No users found';
                }
                else {
                    message = 'Notification sent!';
                }

                dispatch(actions.alerts.notification.push({ message }));
                dispatch(actions.dataFetching.fetchScheduledNotifications());

                if (request.selectedUsers && request.selectedUsers.length){//reset search page
                    dispatch(actions.app.setNotifySearchCriteria({ filter : {} }));
                }
            }
        }),
        uploadPreapproved: makeActionCreator(Types.UPLOAD_PREAPPROVED, {
            schoolId: T.string,
            userByText: T.string,
            notifyUsers: T.bool,
            csv: T.any,
            roleName: T.string
        }, {
            async: api.communication.uploadPreapproved
        }),
        uploadChatImage: makeActionCreator(Types.UPLOAD_CHAT_IMAGE, {
            chatImage: T.any,
            channelSid: T.string
        }, {
            async: api.communication.uploadChatImage
        })
    };
};
